//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useState, useRef } from 'react';
import { pickBy } from 'lodash';
import styled from '@emotion/styled';
import { IconButton, MenuItem, MenuItemProps, TextField } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { FilterProps } from '@/components/_common/Filters/Filter';
import PlatformIcon from '@/components/_common/Table/Calendar/CalendarSharedComponents/PlatformIcon';

type FilterSearchProps = {
  filterId: FilterProps['id'];
  options: Record<string, string> | string[]; // can be an object or array
  label: string;
  selectedOptions: string[];
  handleMenuItemClick: (event: React.MouseEvent | React.KeyboardEvent) => void;
  onClose?: () => void;
  renderAllButtons: (filtered?: Record<string, string> | string[]) => React.ReactNode;
};

const FilterSearch = ({ filterId, options, label, selectedOptions, handleMenuItemClick, onClose, renderAllButtons }: FilterSearchProps) => {
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [textInput, setTextInput] = useState('');
  const optionsIsArray = options.constructor === Array;
  const isPlatforms = filterId === 'availed_platform_filter';

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setTextInput(e.target.value);
    if (optionsIsArray) {
      const filtered = (options as string[]).filter((value) => value.toLowerCase().includes(e.target.value.toLowerCase()));
      setFilteredOptions(filtered);
    } else {
      const filtered = pickBy(options, (value) => value.toLowerCase().includes(e.target.value.toLowerCase()));
      setFilteredOptions(Object.fromEntries(Object.entries(filtered)));
    }
  };

  const clearFilter = () => {
    setTextInput('');
    setFilteredOptions(options);
    searchBoxRef.current?.focus();
  };

  const closeSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  const onInvisibleOptionClicked = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.key === 'Enter') {
      handleMenuItemClick(e);
    } else {
      searchBoxRef.current?.focus();
    }

    closeSearch(e);
  };

  const onWrapperKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    closeSearch(e);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      menuListRef.current?.getElementsByTagName('input')[0]?.focus();
    }
  };

  const sortOptions = (options: string[][]) => {
    return options.sort((a, b) => {
      if (a[1] === 'None' && b[1] !== 'None') return 1;
      if (b[1] === 'None' && a[1] !== 'None') return -1;
      return a[1].localeCompare(b[1]);
    });
  };

  return (
    <div onKeyDown={onWrapperKeyDown}>
      <SearchBox
        inputRef={searchBoxRef}
        role="combobox"
        aria-labelledby="select search"
        aria-haspopup="listbox"
        aria-expanded="true"
        aria-controls="select-dropdown"
        id="program_id"
        autoFocus
        name="filter search"
        autoComplete="off"
        variant="outlined"
        color="primary"
        size="small"
        label={`Quick Search ${label}`}
        value={textInput}
        onChange={onInputChange}
        onKeyDown={handleKeydown}
        InputProps={{
          inputProps: {
            'data-testid': 'filter-search-input'
          },
          tabIndex: 0,
          endAdornment: textInput.length ? (
            <IconButton onClick={clearFilter} tabIndex={Object.keys(filteredOptions).length + 1}>
              <ClearButton icon="close" height={18} />
            </IconButton>
          ) : undefined
        }}
      />
      {renderAllButtons(textInput ? filteredOptions : undefined)}
      <MenuList id="select-dropdown" role="listbox" ref={menuListRef}>
        {sortOptions(Object.entries(filteredOptions)).map(([value, key], index) => {
          const realValue = optionsIsArray ? key : value;
          return (
            <MenuItemWrapped
              data-testid={realValue}
              selected={selectedOptions.includes(realValue)}
              key={realValue}
              id={realValue}
              onKeyDown={(e) => e.stopPropagation()}
              onMouseDown={handleMenuItemClick}
              opacity={value?.toLowerCase() === 'none' ? 0.6 : 1}
            >
              <InvisibleFocusTarget tabIndex={index + 1} id={realValue} onKeyDown={onInvisibleOptionClicked} type="radio" />
              {isPlatforms && <PlatformIcon name={realValue} size="small" disableTooltip />}
              {key}
            </MenuItemWrapped>
          );
        })}
      </MenuList>
    </div>
  );
};

export default FilterSearch;

const InvisibleFocusTarget = styled.input`
  position: absolute;
  opacity: 0;
  left: 0;
`;

const SearchBox = styled(TextField)`
  && {
    margin: ${(props) => props.theme.spacing(2)};
    width: calc(100% - ${(props) => props.theme.spacing(4)});
    min-width: 150px;
    max-width: 300px;

    & .MuiInputBase-root {
      padding-right: 0;
      font-size: 1rem;
      min-height: 50px;
    }

    & .MuiInputLabel-root {
      font-size: 1rem;
      line-height: 2rem;
    }
  }
`;

const MenuList = styled.div`
  max-height: 290px;
  min-width: 150px;
  max-width: 300px;
  white-space: normal;
  overflow: scroll;
  overflow-x: hidden;
`;

const MenuItemWrapped = styled(MenuItem)<MenuItemProps & { opacity: number }>`
  position: relative;
  white-space: normal;
  opacity: ${(props) => props.opacity};
  gap: ${(props) => props.theme.spacing(1)};
`;

const ClearButton = styled(PolarisIcon)`
  margin: ${(props) => props.theme.spacing(1)};
  transition: ease opacity 250ms;

  & svg {
    font-size: 1.2rem;
  }
`;
